/**
 * "Splitting" control shared by the activity editor and the new-activity flow. It lets a person
 * keep the automatic behaviour, forbid splitting, or allow chunks of at least k% each, and shows
 * the chunk sizes the Rust scheduler would produce. The server validates the value again.
 *
 * @module apps/web/src/components/activities/SplitPolicyField
 */
import { useEffect, useId, useState } from 'react';
import type { SplitPolicy } from '@fluxability/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { previewSplitPolicy, type SplitPreviewResult } from '@/lib/splitPolicyPreview';

/** Percentage offered when a person first chooses chunked splitting. */
const DEFAULT_CHUNK_PERCENT = 25;

/** Inputs for the splitting control. */
export interface SplitPolicyFieldProps {
  /** Current policy; undefined means `'auto'`. */
  value: SplitPolicy | undefined;
  /** Receive the edited policy; the parent saves it with the activity. */
  onChange: (policy: SplitPolicy) => void;
  /** Total estimate in minutes, used for the hint. */
  totalMinutes: number;
  /** Unfinished minutes, used for the hint; defaults to the total. */
  remainingMinutes?: number;
  /** Disable editing while the form saves. Defaults to false. */
  disabled?: boolean;
}

/** Select-friendly mode derived from a policy value. */
type SplitMode = 'auto' | 'never' | 'chunks';

/** Map a stored policy to the select's mode. */
function modeOf(policy: SplitPolicy | undefined): SplitMode {
  if (policy && typeof policy === 'object') return 'chunks';
  return policy === 'never' ? 'never' : 'auto';
}

/** Render the splitting choice, the percentage input and the Rust-computed hint. */
export function SplitPolicyField({
  value,
  onChange,
  totalMinutes,
  remainingMinutes,
  disabled = false,
}: SplitPolicyFieldProps) {
  const id = useId();
  const mode = modeOf(value);
  const percent = typeof value === 'object' ? value.min_chunk_percent : DEFAULT_CHUNK_PERCENT;
  const [draft, setDraft] = useState(String(percent));
  const [hint, setHint] = useState<SplitPreviewResult | null>(null);

  useEffect(() => setDraft(String(percent)), [percent]);

  useEffect(() => {
    if (mode !== 'chunks') return;
    let current = true;
    void previewSplitPolicy(totalMinutes, remainingMinutes ?? totalMinutes, percent).then(
      result => {
        if (current) setHint(result);
      }
    );
    return () => {
      current = false;
    };
  }, [mode, percent, totalMinutes, remainingMinutes]);

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${id}-mode`}>Splitting</Label>
      <Select
        value={mode}
        disabled={disabled}
        onValueChange={next => {
          if (next === 'chunks') onChange({ min_chunk_percent: percent });
          else onChange(next as 'auto' | 'never');
        }}
      >
        <SelectTrigger id={`${id}-mode`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Auto (long work may be split)</SelectItem>
          <SelectItem value="never">Don't split</SelectItem>
          <SelectItem value="chunks">Split into chunks of at least…</SelectItem>
        </SelectContent>
      </Select>
      {mode === 'chunks' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>Split into chunks of at least</span>
          <Input
            id={`${id}-percent`}
            aria-label="Minimum chunk percent"
            className="h-8 w-20"
            type="number"
            inputMode="numeric"
            min={5}
            max={100}
            step={5}
            value={draft}
            disabled={disabled}
            onChange={event => {
              setDraft(event.target.value);
              const parsed = Number.parseInt(event.target.value, 10);
              if (Number.isFinite(parsed)) onChange({ min_chunk_percent: parsed });
            }}
          />
          <span>% each</span>
        </div>
      )}
      {mode === 'chunks' && <SplitHint result={hint} percent={percent} />}
    </div>
  );
}

/** Explain the preview Rust returned, or fall back to plain text while it is unavailable.
 * @component SplitHint
 */
function SplitHint({
  result,
  percent,
}: {
  /** Rust preview, or null while loading or unavailable. */
  result: SplitPreviewResult | null;
  /** Percentage the person entered. */
  percent: number;
}) {
  if (!result) {
    return (
      <p className="text-muted-foreground text-xs">
        Each chunk will cover at least {percent}% of this activity.
      </p>
    );
  }
  if (!result.ok) {
    return (
      <p className="text-destructive text-xs" role="alert">
        {result.error}
      </p>
    );
  }
  const { preview } = result;
  const chunks = preview.chunks;
  const now =
    chunks.length > 1
      ? `Now: ${chunks.length} chunks (${chunks.join(' + ')} min).`
      : 'Too short to split right now, so it stays one block.';
  return (
    <p className="text-muted-foreground text-xs">
      Up to {preview.max_chunks} {preview.max_chunks === 1 ? 'chunk' : 'chunks'} of ≥{' '}
      {preview.min_chunk_minutes} min. {now}
    </p>
  );
}
